import React, { useState, useEffect } from 'react';
import SEOHead from '../components/SEOHead';
import { Card, CardContent } from '../components/ui/card';
import { 
  Calendar, Clock, CheckCircle, Video, Phone, Mail, 
  Shield, ExternalLink, Loader2, AlertCircle
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BookingPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);

  useEffect(() => {
    localStorage.setItem('booking_submitted', 'true');
    localStorage.setItem('pricing_unlocked', 'true');
    
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/cms/booking-settings`);
        if (res.ok) {
          const data = await res.json();
          setSettings(data);
        }
      } catch (e) {
        console.error('Failed to fetch booking settings');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const benefits = [
    { icon: Clock, text: "30-minute strategy session" },
    { icon: Video, text: "Video call via Microsoft Teams" },
    { icon: Shield, text: "No obligation, 100% free" },
    { icon: CheckCircle, text: "Personalised demo of GetCare 360" }
  ];

  const microsoftBookingsUrl = settings?.microsoft_bookings_url || 
    "https://outlook.office.com/bookwithme/user/fbf764a9bb5b4c9793065cc702e3b5e6%40getcare360.com.au/meetingtype/sFYthDfSHkCSVKjlKsBDsA2?anonymous&ismsaljsauthenabled";

  const openExternal = () => {
    window.open(microsoftBookingsUrl, '_blank');
  };

  return (
    <>
      <SEOHead 
        title="Book a Free Strategy Call | GetCare 360"
        description="Schedule a free 30-minute strategy call with our NDIS software experts. Learn how GetCare 360 can transform your operations."
        path="/booking"
      />
      
      <div className="min-h-screen bg-page-bg">
        <Header />
        
        <main className="pt-24 pb-16">
          <div className="container mx-auto px-4">
            {/* Hero Section */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-tech-blue/10 rounded-full text-tech-blue text-sm font-medium mb-4">
                <Calendar size={16} />
                Free Strategy Call
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary-navy mb-4">
                Book Your Free Demo
              </h1>
              <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                Schedule a 30-minute call with our NDIS software experts. 
                See how GetCare 360 can transform your operations.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {/* Benefits Sidebar */}
              <div className="lg:col-span-1 order-2 lg:order-1">
                <Card className="border-border-line sticky top-24">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-primary-navy mb-4">
                      What You'll Get
                    </h3>
                    <div className="space-y-4">
                      {benefits.map((benefit, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-lg bg-care-teal/10 flex items-center justify-center flex-shrink-0">
                            <benefit.icon className="text-care-teal" size={16} />
                          </div>
                          <span className="text-text-secondary text-sm pt-1">{benefit.text}</span>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 pt-6 border-t border-border-line">
                      <h4 className="text-sm font-semibold text-primary-navy mb-3">
                        We'll Discuss
                      </h4>
                      <ul className="space-y-2 text-sm text-text-secondary">
                        <li className="flex items-center gap-2">
                          <CheckCircle size={14} className="text-care-teal" />
                          Your current NDIS challenges
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle size={14} className="text-care-teal" />
                          Custom features for your needs
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle size={14} className="text-care-teal" />
                          One-time pricing options
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle size={14} className="text-care-teal" />
                          Implementation timeline
                        </li>
                      </ul>
                    </div>

                    <div className="mt-6 pt-6 border-t border-border-line">
                      <h4 className="text-sm font-semibold text-primary-navy mb-3">
                        Contact Us Directly
                      </h4>
                      <div className="space-y-2">
                        <a href="tel:1800951438" className="flex items-center gap-2 text-sm text-tech-blue hover:text-primary-navy transition-colors">
                          <Phone size={14} />
                          1800 951 GET
                        </a>
                        <a href="mailto:info@getcare360.com.au" className="flex items-center gap-2 text-sm text-tech-blue hover:text-primary-navy transition-colors">
                          <Mail size={14} />
                          info@getcare360.com.au
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Microsoft Bookings Embed */}
              <div className="lg:col-span-2 order-1 lg:order-2">
                <Card className="border-border-line overflow-hidden">
                  <CardContent className="p-0">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-4 text-white">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                            <Calendar className="text-white" size={20} />
                          </div>
                          <div>
                            <h2 className="font-semibold">Select Your Time</h2>
                            <p className="text-sm text-white/70">Choose a convenient slot</p>
                          </div>
                        </div>
                        <button 
                          onClick={openExternal}
                          className="flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors bg-white/10 px-3 py-2 rounded-lg"
                        >
                          <ExternalLink size={14} />
                          <span className="hidden sm:inline">Open in new tab</span>
                        </button>
                      </div>
                    </div>

                    {/* Booking Content */}
                    <div className="relative" style={{ height: '600px' }}>
                      {/* Loading State */}
                      {iframeLoading && !iframeError && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
                          <div className="text-center">
                            <Loader2 className="w-10 h-10 animate-spin text-slate-600 mx-auto mb-3" />
                            <p className="text-gray-600 font-medium">Loading booking calendar...</p>
                          </div>
                        </div>
                      )}

                      {/* Error/Fallback State */}
                      {iframeError && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10 p-6">
                          <div className="text-center max-w-sm">
                            <AlertCircle className="w-12 h-12 text-orange-500 mx-auto mb-4" />
                            <h3 className="text-lg font-bold text-gray-800 mb-2">
                              Calendar Preview Unavailable
                            </h3>
                            <p className="text-gray-600 mb-6">
                              Click below to open the booking calendar.
                            </p>
                            <button
                              onClick={openExternal}
                              className="bg-slate-800 hover:bg-slate-700 text-white font-semibold px-6 py-3 rounded-lg flex items-center justify-center gap-2 mx-auto transition-colors"
                            >
                              <Calendar size={20} />
                              Open Booking Page
                              <ExternalLink size={18} />
                            </button>
                          </div>
                        </div>
                      )}

                      {/* iframe */}
                      <iframe
                        src={microsoftBookingsUrl}
                        className="w-full h-full border-0"
                        title="Book a Demo - Microsoft Bookings"
                        onLoad={() => setIframeLoading(false)}
                        onError={() => { setIframeLoading(false); setIframeError(true); }}
                        allow="camera; microphone"
                        data-testid="microsoft-bookings-iframe"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Trust Badges */}
                <div className="mt-6 flex flex-wrap items-center justify-center gap-6 text-sm text-text-secondary">
                  <div className="flex items-center gap-2">
                    <Shield className="text-care-teal" size={16} />
                    <span>100% Secure</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="text-care-teal" size={16} />
                    <span>No Credit Card Required</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="text-care-teal" size={16} />
                    <span>Instant Confirmation</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default BookingPage;
